import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  useToast,
  Card,
  CardHeader,
  CardBody,
  Heading,
  useColorModeValue,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Progress,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Textarea,
  Switch,
} from '@chakra-ui/react';
import { FiSearch, FiDownload, FiFilter, FiPlay, FiPause, FiX, FiClock, FiUser, FiCalendar, FiFileText, FiDownload as FiDownloadRecording } from 'react-icons/fi';

const CallHistory = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCall, setSelectedCall] = useState(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Dummy data for call history
  const [calls, setCalls] = useState([
    {
      id: 1,
      phoneNumber: '+1 (555) 123-4567',
      agent: 'Sales Agent',
      status: 'completed',
      duration: '5:30',
      startTime: '2024-03-15 09:30',
      endTime: '2024-03-15 09:35',
      type: 'outbound',
      sentiment: 'positive',
      transcript: 'Hello, this is a sales call...',
      tags: ['sales', 'follow-up'],
      recording: 'https://example.com/recordings/call-1.mp3',
    },
    {
      id: 2,
      phoneNumber: '+1 (555) 987-6543',
      agent: 'Support Agent',
      status: 'failed',
      duration: '0:45',
      startTime: '2024-03-15 10:15',
      endTime: '2024-03-15 10:16',
      type: 'inbound',
      sentiment: 'neutral',
      transcript: 'Thank you for calling support...',
      tags: ['support', 'technical'],
      recording: null,
    },
    {
      id: 3,
      phoneNumber: '+1 (555) 456-7890',
      agent: 'Survey Agent',
      status: 'completed',
      duration: '8:15',
      startTime: '2024-03-15 11:20',
      endTime: '2024-03-15 11:28',
      type: 'outbound',
      sentiment: 'positive',
      transcript: 'We would like to get your feedback...',
      tags: ['survey', 'feedback'],
      recording: 'https://example.com/recordings/call-3.mp3',
    },
    {
      id: 4,
      phoneNumber: '+1 (555) 789-0123',
      agent: 'Demo Agent',
      status: 'missed',
      duration: '0:00',
      startTime: '2024-03-15 13:45',
      endTime: '2024-03-15 13:45',
      type: 'inbound',
      sentiment: 'neutral',
      transcript: '',
      tags: ['demo', 'missed'],
      recording: null,
    },
  ]);

  const handleViewCall = (call) => {
    setSelectedCall(call);
    onOpen();
  };

  const handleDownloadRecording = (url) => {
    toast({
      title: 'Download Started',
      description: 'The call recording is being downloaded.',
      status: 'info',
      duration: 3000,
    });
  };

  const filteredCalls = calls.filter(call =>
    call.phoneNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
    call.agent.toLowerCase().includes(searchQuery.toLowerCase()) ||
    call.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Calculate statistics
  const totalCalls = calls.length;
  const completedCalls = calls.filter(call => call.status === 'completed').length;
  const totalDuration = calls.reduce((sum, call) => {
    const [minutes, seconds] = call.duration.split(':').map(Number);
    return sum + (minutes * 60 + seconds);
  }, 0);
  const averageDuration = Math.round(totalDuration / calls.length / 60);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      case 'missed':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  const getSentimentColor = (sentiment) => {
    switch (sentiment) {
      case 'positive':
        return 'green';
      case 'negative':
        return 'red';
      case 'neutral':
        return 'yellow';
      default:
        return 'gray';
    }
  };

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <HStack justify="space-between">
          <Heading size="lg">Call History</Heading>
          <HStack>
            <Button
              leftIcon={<FiDownload />}
              variant="outline"
              onClick={() => {
                toast({
                  title: 'Export Started',
                  description: 'Exporting call history data...',
                  status: 'info',
                  duration: 3000,
                });
              }}
            >
              Export
            </Button>
            <Menu>
              <MenuButton
                as={Button}
                leftIcon={<FiFilter />}
                variant="outline"
              >
                Filter
              </MenuButton>
              <MenuList>
                <MenuItem>All Calls</MenuItem>
                <MenuItem>Completed</MenuItem>
                <MenuItem>Failed</MenuItem>
                <MenuItem>Missed</MenuItem>
              </MenuList>
            </Menu>
          </HStack>
        </HStack>

        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={6}>
          <Card bg={bgColor} boxShadow="md" borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Stat>
                <StatLabel>Total Calls</StatLabel>
                <StatNumber>{totalCalls}</StatNumber>
                <StatHelpText>All time</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={bgColor} boxShadow="md" borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Stat>
                <StatLabel>Completed Calls</StatLabel>
                <StatNumber>{completedCalls}</StatNumber>
                <StatHelpText>Successfully completed</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={bgColor} boxShadow="md" borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Stat>
                <StatLabel>Average Duration</StatLabel>
                <StatNumber>{averageDuration} min</StatNumber>
                <StatHelpText>Per call</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
          <Card bg={bgColor} boxShadow="md" borderColor={borderColor} borderWidth="1px">
            <CardBody>
              <Stat>
                <StatLabel>Success Rate</StatLabel>
                <StatNumber>{Math.round((completedCalls / totalCalls) * 100)}%</StatNumber>
                <StatHelpText>Call completion rate</StatHelpText>
              </Stat>
            </CardBody>
          </Card>
        </SimpleGrid>

        <Card bg={bgColor} boxShadow="md" borderColor={borderColor} borderWidth="1px">
          <CardHeader>
            <InputGroup>
              <InputLeftElement pointerEvents="none">
                <FiSearch color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search calls..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
          </CardHeader>
          <CardBody>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>Phone Number</Th>
                  <Th>Agent</Th>
                  <Th>Status</Th>
                  <Th>Duration</Th>
                  <Th>Start Time</Th>
                  <Th>Type</Th>
                  <Th>Sentiment</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredCalls.map((call) => (
                  <Tr key={call.id}>
                    <Td>{call.phoneNumber}</Td>
                    <Td>{call.agent}</Td>
                    <Td>
                      <Badge colorScheme={getStatusColor(call.status)}>
                        {call.status}
                      </Badge>
                    </Td>
                    <Td>{call.duration}</Td>
                    <Td>{call.startTime}</Td>
                    <Td>
                      <Badge colorScheme={call.type === 'inbound' ? 'blue' : 'purple'}>
                        {call.type}
                      </Badge>
                    </Td>
                    <Td>
                      <Badge colorScheme={getSentimentColor(call.sentiment)}>
                        {call.sentiment}
                      </Badge>
                    </Td>
                    <Td>
                      <HStack spacing={2}>
                        <IconButton
                          icon={<FiPlay />}
                          size="sm"
                          onClick={() => handleViewCall(call)}
                          aria-label="View call"
                        />
                        {call.recording && (
                          <IconButton
                            icon={<FiDownload />}
                            size="sm"
                            onClick={() => handleDownloadRecording(call.recording)}
                            aria-label="Download recording"
                          />
                        )}
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </VStack>

      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Call Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            {selectedCall && (
              <VStack spacing={4} align="stretch">
                <Box>
                  <Text fontWeight="bold">Phone Number</Text>
                  <Text>{selectedCall.phoneNumber}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Agent</Text>
                  <Text>{selectedCall.agent}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Status</Text>
                  <Badge colorScheme={getStatusColor(selectedCall.status)}>
                    {selectedCall.status}
                  </Badge>
                </Box>
                <Box>
                  <Text fontWeight="bold">Duration</Text>
                  <Text>{selectedCall.duration}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Start Time</Text>
                  <Text>{selectedCall.startTime}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">End Time</Text>
                  <Text>{selectedCall.endTime}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Type</Text>
                  <Badge colorScheme={selectedCall.type === 'inbound' ? 'blue' : 'purple'}>
                    {selectedCall.type}
                  </Badge>
                </Box>
                <Box>
                  <Text fontWeight="bold">Sentiment</Text>
                  <Badge colorScheme={getSentimentColor(selectedCall.sentiment)}>
                    {selectedCall.sentiment}
                  </Badge>
                </Box>
                <Box>
                  <Text fontWeight="bold">Transcript</Text>
                  <Text>{selectedCall.transcript}</Text>
                </Box>
                <Box>
                  <Text fontWeight="bold">Tags</Text>
                  <HStack>
                    {selectedCall.tags.map((tag, index) => (
                      <Badge key={index} colorScheme="blue">
                        {tag}
                      </Badge>
                    ))}
                  </HStack>
                </Box>
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CallHistory;